import React, {useEffect, useRef, useState} from 'react';
import './style.css';
import api from "../../api";
import {useNavigate, useParams} from "react-router-dom";
import {Button, Col, Row, DatePicker, Select, Modal, Form, Drawer, Checkbox, Slider} from "antd";
import PlantingPreview from "../../Components/PlantingPreview";
import {ZoomInOutlined, ZoomOutOutlined, LeftOutlined, RightOutlined, SettingFilled, PlusOutlined, PrinterOutlined} from "@ant-design/icons";
import {useTranslations} from "../../lang";
import {calculateRowsAndPlants} from "../../helpers";
import PlantingModal from "../../Components/PlantingModal";
import PlantingDrawer from "../../Components/PlantingDrawer";
import BedModal from "../../Components/BedModal";
import dayjs from "dayjs";

const GardenMap = () => {
    const [bedPositions, setBedPositions] = useState([]);
    const [garden, setGarden] = useState(null);
    const [resizingIndex, setResizingIndex] = useState(null);
    const snapToGrid = 5;
    const containerRef = useRef(null);
    const activeDragCleanup = useRef(null);

    useEffect(() => () => activeDragCleanup.current?.(), []);

    const localZoomLevel = localStorage.getItem('zoomLevel');
    const [zoomLevel, setZoomLevel] = useState(localZoomLevel ? parseFloat(localZoomLevel) : 0);
    // Calculate zoom: -5 = 0.05x, 0 = 1.0x, +5 = 5x
    const zoom = zoomLevel === 0 ? 1 : (zoomLevel > 0 ? 1 + (zoomLevel * 0.8) : 1 + (zoomLevel * 0.19));
    const {translate} = useTranslations();
    const navigate = useNavigate();

    const defaultDateRangeLocal = localStorage.getItem('gardenMapDateRange');
    const [defaultDateRange, setDefaultDateRange] = useState(defaultDateRangeLocal ?? "next_12_months");
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs().add(11, 'months'));
    const [showCustomDateModal, setShowCustomDateModal] = useState(false);
    const [customDateRange, setCustomDateRange] = useState([null, null]);

    // Display settings
    const hideCropVarietiesLocal = localStorage.getItem('gardenMapHideCropVarieties');
    const [hideCropVarieties, setHideCropVarieties] = useState(hideCropVarietiesLocal ? hideCropVarietiesLocal === "true" : false);
    const [showDisplayDrawer, setShowDisplayDrawer] = useState(false);

    // Track which planting is currently visible for each bed (for beds with multiple plantings)
    const [activePlantingIndex, setActivePlantingIndex] = useState({});

    // Planting modal state
    const [plantingModalOpen, setPlantingModalOpen] = useState(false);
    const [selectedBed, setSelectedBed] = useState(null);

    // Bed modal state
    const [bedModalOpen, setBedModalOpen] = useState(false);

    // Planting drawer state
    const [plantingDrawerOpen, setPlantingDrawerOpen] = useState(false);
    const [selectedPlanting, setSelectedPlanting] = useState(null);
    const [selectedPlantingBedId, setSelectedPlantingBedId] = useState(null);
    const dragOccurred = useRef(false);

    const setPreSelectedDateRange = (selection) => {
        if (selection === "custom") {
            setShowCustomDateModal(true);
            return;
        }

        localStorage.setItem('gardenMapDateRange', selection);
        setDefaultDateRange(selection);
        if (selection === "today_end_of_year") {
            setStartDate(dayjs().startOf('month'));
            setEndDate(dayjs().endOf('year'));
        }
        if (selection === "start_of_year_today") {
            setStartDate(dayjs().startOf('year'));
            setEndDate(dayjs());
        }
        if (selection === "this_year") {
            setStartDate(dayjs().startOf('year'));
            setEndDate(dayjs().endOf('year'));
        }
        if (selection === "last_year") {
            setStartDate(dayjs().startOf('year').subtract(1, 'year'));
            setEndDate(dayjs().endOf('year').subtract(1, 'year'));
        }
        if (selection === "next_year") {
            setStartDate(dayjs().startOf('year').add(1, 'year'));
            setEndDate(dayjs().endOf('year').add(1, 'year'));
        }
        if (selection === "next_12_months") {
            setStartDate(dayjs().startOf('month'));
            setEndDate(dayjs().add(11, 'months'));
        }
        if (selection === "next_3_months") {
            setStartDate(dayjs().startOf('month'));
            setEndDate(dayjs().add(3, 'months'));
        }
    };

    const handleCustomDateRangeApply = () => {
        if (customDateRange[0] && customDateRange[1]) {
            setStartDate(dayjs(customDateRange[0]));
            setEndDate(dayjs(customDateRange[1]));
            localStorage.setItem('gardenMapDateRange', 'custom');
            setDefaultDateRange('custom');
            setShowCustomDateModal(false);
        }
    };

    const isPlantingInGround = (planting) => {
        if (!planting.transplant_at || !planting.harvest_until) return false;
        const transplantDate = dayjs(planting.transplant_at);
        const harvestUntilDate = dayjs(planting.harvest_until);
        return transplantDate.isBefore(endDate) && harvestUntilDate.isAfter(startDate);
    };

    // Track which crop IDs we've already requested auto-icons for
    const autoIconRequested = useRef(new Set());

    const {id} = useParams();
    useEffect(() => {
        api.garden(id).then((gardenData) => {
            setGarden(gardenData);
            setBedPositions(gardenData.beds.map(((bed, i) => ({
                ...bed,
                left: bed.left !== null && bed.left !== undefined ? bed.left : snapToGrid,
                top: bed.top !== null && bed.top !== undefined ? bed.top : (i * bed.width + i * snapToGrid)
            }))));
        });
    }, [id]);

    // Auto-assign icons for crops that don't have one
    useEffect(() => {
        if (!bedPositions || bedPositions.length === 0) return;

        const cropsWithoutIcons = [];
        bedPositions.forEach(bed => {
            bed.plantings?.forEach(planting => {
                if (planting.crop && !planting.crop.icon && !autoIconRequested.current.has(planting.crop.id)) {
                    cropsWithoutIcons.push(planting.crop);
                    autoIconRequested.current.add(planting.crop.id);
                }
            });
        });

        cropsWithoutIcons.forEach(crop => {
            api.autoAssignCropIcon(crop.id).then(icon => {
                if (icon && icon.url) {
                    setBedPositions(prev => prev.map(bed => ({
                        ...bed,
                        plantings: bed.plantings?.map(p => {
                            if (p.crop && p.crop.id === crop.id) {
                                return {...p, crop: {...p.crop, icon: {id: icon.id, url: icon.url}}};
                            }
                            return p;
                        }) || []
                    })));
                }
            }).catch(() => {});
        });
    }, [bedPositions.length]);

    useEffect(() => {
        if (defaultDateRange && defaultDateRange !== 'custom') {
            setPreSelectedDateRange(defaultDateRange);
        }
    }, []);

    const handlePointerDown = (index, e) => {
        e.preventDefault();
        dragOccurred.current = false;
        containerRef.current.style.touchAction = 'none';
        startDragging(index, e);
    };

    const openPlantingDrawer = (e, planting, bedId) => {
        if (dragOccurred.current) return;
        e.stopPropagation();
        e.preventDefault();
        setSelectedPlanting(planting);
        setSelectedPlantingBedId(bedId);
        setPlantingDrawerOpen(true);
    };

    const startDragging = (index, e) => {
        e.preventDefault();
        document.body.style.overflow = 'hidden';

        const containerRect = containerRef.current.getBoundingClientRect();
        const startX = ((e.clientX ?? e.touches[0].clientX) - containerRect.left - bedPositions[index].left * zoom) / zoom;
        const startY = ((e.clientY ?? e.touches[0].clientY) - containerRect.top - bedPositions[index].top * zoom) / zoom;

        const onPointerMove = (e) => {
            e.preventDefault();
            dragOccurred.current = true;
            const updatedBeds = [...bedPositions];
            let newLeft = ((e.clientX ?? e.touches[0].clientX) - containerRect.left) / zoom - startX;
            let newTop = ((e.clientY ?? e.touches[0].clientY) - containerRect.top) / zoom - startY;

            if (newLeft < 0) {
                newLeft = 0;
            }
            if (newTop < 0) {
                newTop = 0;
            }

            if (snapToGrid > 0) {
                newLeft = Math.round(newLeft / snapToGrid) * snapToGrid;
                newTop = Math.round(newTop / snapToGrid) * snapToGrid;
            }

            updatedBeds[index].left = newLeft;
            updatedBeds[index].top = newTop;
            setBedPositions(updatedBeds);
        };

        const cleanupDrag = () => {
            if (containerRef.current) {
                containerRef.current.style.touchAction = '';
            }
            document.removeEventListener('mousemove', onPointerMove);
            document.removeEventListener('mouseup', onPointerUp);
            document.removeEventListener('touchmove', onPointerMove);
            document.removeEventListener('touchend', onPointerUp);
            document.body.style.overflow = '';
            activeDragCleanup.current = null;
        };

        const onPointerUp = () => {
            cleanupDrag();

            api.setBedPositions(id, bedPositions.map((bed) => ({
                left: bed.left,
                top: bed.top,
                id: bed.id
            })));
        };

        activeDragCleanup.current?.();
        activeDragCleanup.current = cleanupDrag;
        document.addEventListener('mousemove', onPointerMove);
        document.addEventListener('mouseup', onPointerUp);
        document.addEventListener('touchmove', onPointerMove);
        document.addEventListener('touchend', onPointerUp);
    };

    const handleResizeStart = (index, e) => {
        e.preventDefault();
        e.stopPropagation();
        setResizingIndex(index);
        document.body.style.overflow = 'hidden';

        const bed = bedPositions[index];
        const startMouseX = (e.clientX ?? e.touches[0].clientX);
        const startMouseY = (e.clientY ?? e.touches[0].clientY);
        const startWidth = bed.length;
        const startHeight = bed.width;

        // Track the current dimensions during resize
        let currentLength = startWidth;
        let currentWidth = startHeight;

        const onResizeMove = (e) => {
            e.preventDefault();
            const currentX = (e.clientX ?? e.touches[0].clientX);
            const currentY = (e.clientY ?? e.touches[0].clientY);

            const deltaX = (currentX - startMouseX) / zoom;
            const deltaY = (currentY - startMouseY) / zoom;

            let newLength = startWidth + deltaX;
            let newWidth = startHeight + deltaY;

            // Minimum size constraints
            const minSize = 20;
            if (newLength < minSize) newLength = minSize;
            if (newWidth < minSize) newWidth = minSize;

            // Snap to grid
            if (snapToGrid > 0) {
                newLength = Math.round(newLength / snapToGrid) * snapToGrid;
                newWidth = Math.round(newWidth / snapToGrid) * snapToGrid;
            }

            // Update the tracked dimensions
            currentLength = newLength;
            currentWidth = newWidth;

            setBedPositions(currentBeds => {
                const updatedBeds = [...currentBeds];
                updatedBeds[index] = {
                    ...updatedBeds[index],
                    length: newLength,
                    width: newWidth
                };
                return updatedBeds;
            });
        };

        const onResizeEnd = () => {
            setResizingIndex(null);
            document.removeEventListener('mousemove', onResizeMove);
            document.removeEventListener('mouseup', onResizeEnd);
            document.removeEventListener('touchmove', onResizeMove);
            document.removeEventListener('touchend', onResizeEnd);
            document.body.style.overflow = '';

            // Save the updated bed dimensions using tracked values
            api.updateBed(id, bed.id, {
                width: currentWidth,
                length: currentLength
            }).then(() => {
                // Recalculate plant counts for all plantings in this bed
                recalculatePlantings(index);
            });
        };

        document.addEventListener('mousemove', onResizeMove);
        document.addEventListener('mouseup', onResizeEnd);
        document.addEventListener('touchmove', onResizeMove);
        document.addEventListener('touchend', onResizeEnd);
    };

    const recalculatePlantings = (bedIndex) => {
        setBedPositions(currentBeds => {
            const bed = currentBeds[bedIndex];
            const plantings = bed.plantings.filter(p => p.status !== "harvested" && p.crop);

            // Calculate new values for all plantings first
            const updates = plantings.map(planting => {
                const {rows, plants_per_row} = calculateRowsAndPlants(bed, planting.crop);
                return { planting, rows, plants_per_row };
            });

            // Update local state immediately with new calculations
            const updatedBeds = [...currentBeds];
            updatedBeds[bedIndex] = {
                ...updatedBeds[bedIndex],
                plantings: updatedBeds[bedIndex].plantings.map(p => {
                    const update = updates.find(u => u.planting.id === p.id);
                    if (update) {
                        return { ...p, rows: update.rows, plants_per_row: update.plants_per_row };
                    }
                    return p;
                })
            };

            // Save to API in background
            updates.forEach(({ planting, rows, plants_per_row }) => {
                api.updatePlanting(planting.id, {
                    rows: rows,
                    plants_per_row: plants_per_row
                });
            });

            return updatedBeds;
        });
    };

    const getRotationAngle = (bed) => {
        return bed.rotation || 0;
    };

    function handleZoomChange(newZoomLevel) {
        localStorage.setItem('zoomLevel', newZoomLevel);
        setZoomLevel(newZoomLevel);
    }

    const formatDimensions = (bed) => {
        const width = bed.width >= 100 ? `${(bed.width / 100).toFixed(1)}m` : `${bed.width}cm`;
        const length = bed.length >= 100 ? `${(bed.length / 100).toFixed(1)}m` : `${bed.length}cm`;
        return `${width} × ${length}`;
    };

    const setHideCropVarietiesLocal = (e) => {
        const checked = e.target.checked;
        localStorage.setItem('gardenMapHideCropVarieties', checked);
        setHideCropVarieties(checked);
    };

    // Get the current planting index for a bed
    const getActivePlantingIndex = (bedId, totalPlantings) => {
        const index = activePlantingIndex[bedId] || 0;
        return Math.min(index, totalPlantings - 1);
    };

    // Navigate to next planting in a bed
    const nextPlanting = (e, bedId, totalPlantings) => {
        e.stopPropagation();
        e.preventDefault();
        setActivePlantingIndex(prev => ({
            ...prev,
            [bedId]: ((prev[bedId] || 0) + 1) % totalPlantings
        }));
    };

    // Navigate to previous planting in a bed
    const prevPlanting = (e, bedId, totalPlantings) => {
        e.stopPropagation();
        e.preventDefault();
        setActivePlantingIndex(prev => ({
            ...prev,
            [bedId]: ((prev[bedId] || 0) - 1 + totalPlantings) % totalPlantings
        }));
    };

    const showPlantingModal = (e, bed) => {
        e.stopPropagation();
        e.preventDefault();
        setSelectedBed(bed);
        setPlantingModalOpen(true);
    };

    const handleBedModalClose = () => {
        setBedModalOpen(false);
        // Reload garden to reflect the new bed
        api.garden(id).then((gardenData) => {
            setGarden(gardenData);
            setBedPositions(gardenData.beds.map(((bed, i) => ({
                ...bed,
                left: bed.left !== null && bed.left !== undefined ? bed.left : snapToGrid,
                top: bed.top !== null && bed.top !== undefined ? bed.top : (i * bed.width + i * snapToGrid)
            }))));
        });
    };

    const reloadGarden = () => {
        api.garden(id).then((gardenData) => {
            setGarden(gardenData);
            setBedPositions(gardenData.beds.map(((bed, i) => ({
                ...bed,
                left: bed.left !== null && bed.left !== undefined ? bed.left : snapToGrid,
                top: bed.top !== null && bed.top !== undefined ? bed.top : (i * bed.width + i * snapToGrid)
            }))));
        });
    };

    const handlePlantingDrawerClose = () => {
        setPlantingDrawerOpen(false);
        setSelectedPlanting(null);
        setSelectedPlantingBedId(null);
        reloadGarden();
    };

    const handlePlantingAdded = (planting) => {
        setPlantingModalOpen(false);
        setSelectedBed(null);
        // Reload garden to reflect the new planting
        api.garden(id).then((gardenData) => {
            setGarden(gardenData);
            setBedPositions(gardenData.beds.map(((bed, i) => ({
                ...bed,
                left: bed.left !== null && bed.left !== undefined ? bed.left : snapToGrid,
                top: bed.top !== null && bed.top !== undefined ? bed.top : (i * bed.width + i * snapToGrid)
            }))));
        });
    };

    return (
        <div>
            <Row>
                <Col span={24}>
                    <div className="garden-map-toolbar">
                        <Button onClick={() => navigate(`/planner/${id}`)}>{translate('Terug naar planner')}</Button>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', minWidth: '200px'}}>
                            <ZoomOutOutlined />
                            <Slider
                                min={-5}
                                max={5}
                                value={zoomLevel}
                                onChange={handleZoomChange}
                                style={{flex: 1}}
                                tooltip={{formatter: (value) => {
                                    const zoomValue = value === 0 ? 1 : (value > 0 ? 1 + (value * 0.8) : 1 + (value * 0.19));
                                    return `${zoomValue.toFixed(2)}x`;
                                }}}
                            />
                            <ZoomInOutlined />
                        </div>
                        <Select
                            value={defaultDateRange}
                            style={{minWidth: '180px'}}
                            onChange={(e) => setPreSelectedDateRange(e)}
                            options={[
                                {label: translate("Vandaag tot einde jaar"), value: "today_end_of_year"},
                                {label: translate("Begin dit jaar tot vandaag"), value: "start_of_year_today"},
                                {label: translate("Dit jaar"), value: "this_year"},
                                {label: translate("Vorig jaar"), value: "last_year"},
                                {label: translate("Komende 3 maanden"), value: "next_3_months"},
                                {label: translate("Komende 12 maanden"), value: "next_12_months"},
                                {label: translate("Zelf selecteren"), value: "custom"}
                            ]}
                        />
                        <Button onClick={() => setBedModalOpen(true)} icon={<PlusOutlined/>}>
                            {translate('Bed toevoegen')}
                        </Button>
                        <Button onClick={() => setShowDisplayDrawer(true)} icon={<SettingFilled/>}>
                            {translate('Weergave instellingen')}
                        </Button>
                        <Button onClick={() => window.print()} icon={<PrinterOutlined/>}>
                            {translate('Printen')}
                        </Button>
                    </div>
                </Col>
            </Row>

            <Modal
                title={translate("Selecteer datumbereik")}
                open={showCustomDateModal}
                onCancel={() => {
                    setShowCustomDateModal(false);
                    setDefaultDateRange(defaultDateRange === 'custom' ? 'next_12_months' : defaultDateRange);
                }}
                onOk={handleCustomDateRangeApply}
                okText={translate("Toepassen")}
                cancelText={translate("Annuleren")}
            >
                <Form layout="vertical">
                    <Form.Item label={translate("Kies een datumbereik")}>
                        <DatePicker.RangePicker
                            style={{width: '100%'}}
                            format="DD-MM-YYYY"
                            value={customDateRange[0] && customDateRange[1] ? [dayjs(customDateRange[0]), dayjs(customDateRange[1])] : null}
                            onChange={(dates) => {
                                if (dates) {
                                    setCustomDateRange([dates[0], dates[1]]);
                                } else {
                                    setCustomDateRange([null, null]);
                                }
                            }}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Drawer open={showDisplayDrawer} onClose={() => setShowDisplayDrawer(false)}>
                <h2>{translate('Weergave instellingen')}</h2>
                <Form.Item
                    help={translate("Stel, je kweekt meerdere rassen van bijvoorbeeld tomaten. Dan is het handig om te zien om welk ras het gaat. Als je dit ook wilt zien op de kaart moet je dit uitvinken")}>
                    <Checkbox checked={hideCropVarieties}
                              onChange={setHideCropVarietiesLocal}></Checkbox> {translate('Verberg rassen van gewassen')}
                </Form.Item>
            </Drawer>

            <PlantingModal
                open={plantingModalOpen}
                onClose={() => {
                    setPlantingModalOpen(false);
                    setSelectedBed(null);
                }}
                bed={selectedBed}
                onPlantingAdded={handlePlantingAdded}
            />

            <Drawer open={plantingDrawerOpen} size="large" onClose={handlePlantingDrawerClose}>
                {selectedPlanting && (
                    <PlantingDrawer
                        gardenId={parseInt(id)}
                        plantingId={selectedPlanting.id}
                        bedId={selectedPlantingBedId}
                        onCloseDrawer={handlePlantingDrawerClose}
                    />
                )}
            </Drawer>

            {garden && (
                <BedModal
                    garden={garden}
                    open={bedModalOpen}
                    onCancel={handleBedModalClose}
                />
            )}

            <div className="garden-print-header">
                {garden?.name} — {startDate.format('DD-MM-YYYY')} t/m {endDate.format('DD-MM-YYYY')}
            </div>

            <div ref={containerRef} className="bed-container">
                {garden && garden.width && garden.length && (
                    <div
                        className="garden-outline"
                        style={{
                            width: `${garden.width * 100 * zoom}px`,
                            height: `${garden.length * 100 * zoom}px`,
                        }}
                    >
                        <div className="garden-outline-label">
                            {garden.width}m × {garden.length}m
                        </div>
                    </div>
                )}
                {bedPositions.map((bed, index) => {
                    const visiblePlantings = bed.plantings.filter(planting => planting.status !== "harvested" && isPlantingInGround(planting));
                    const isEmpty = visiblePlantings.length === 0;
                    return (
                    <div
                        key={bed.id || bed.name}
                        className={`bed ${resizingIndex === index ? 'resizing' : ''} ${isEmpty ? 'bed-empty' : ''}`}
                        style={{
                            height: `${bed.width * zoom}px`,
                            width: `${bed.length * zoom}px`,
                            top: `${bed.top * zoom}px`,
                            left: `${bed.left * zoom}px`,
                            transform: `rotate(${getRotationAngle(bed)}deg)`,
                        }}
                        onMouseDown={(e) => handlePointerDown(index, e)}
                        onTouchStart={(e) => handlePointerDown(index, e)}
                    >
                        <div className="bed-tooltip">
                            {bed.name}
                        </div>

                        <div className="bed-dimensions">
                            {formatDimensions(bed)}
                        </div>

                        <div
                            className="bed-resize-handle bottom-right"
                            onMouseDown={(e) => handleResizeStart(index, e)}
                            onTouchStart={(e) => handleResizeStart(index, e)}
                        />

                        <button
                            className="bed-add-planting-btn"
                            onClick={(e) => showPlantingModal(e, bed)}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                        >
                            <PlusOutlined />
                        </button>

                        {(() => {
                            const hasMultiplePlantings = visiblePlantings.length > 1;
                            const activeIndex = getActivePlantingIndex(bed.id, visiblePlantings.length);
                            const activePlanting = visiblePlantings[activeIndex];

                            if (!activePlanting) return null;

                            const label = hideCropVarieties || !activePlanting.crop.variety
                                ? activePlanting.crop.name
                                : `${activePlanting.crop.name} - ${activePlanting.crop.variety}`;

                            return (
                                <>
                                    <div
                                        className="planting-clickable"
                                        onClick={(e) => openPlantingDrawer(e, activePlanting, bed.id)}
                                        style={{cursor: 'pointer', width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, zIndex: 1}}
                                    >
                                        <PlantingPreview
                                            label={label}
                                            key={activePlanting.id}
                                            bedWidth={bed.length * zoom}
                                            bedLength={bed.width * zoom}
                                            plantsPerRow={activePlanting.plants_per_row}
                                            numRows={activePlanting.rows}
                                            iconUrl={activePlanting.crop?.icon?.url}
                                        />
                                    </div>
                                    {hasMultiplePlantings && (
                                        <div className="planting-nav-buttons">
                                            <button
                                                className="planting-nav-btn planting-nav-prev"
                                                onClick={(e) => prevPlanting(e, bed.id, visiblePlantings.length)}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onTouchStart={(e) => e.stopPropagation()}
                                            >
                                                <LeftOutlined />
                                            </button>
                                            <span className="planting-nav-indicator">
                                                {activeIndex + 1}/{visiblePlantings.length}
                                            </span>
                                            <button
                                                className="planting-nav-btn planting-nav-next"
                                                onClick={(e) => nextPlanting(e, bed.id, visiblePlantings.length)}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onTouchStart={(e) => e.stopPropagation()}
                                            >
                                                <RightOutlined />
                                            </button>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                    );
                })}
            </div>
        </div>
    );
};

export default GardenMap;
